import { useMemo } from 'react';
import { useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import { periodOptions, quickPeriods, type Period } from '@/lib/dates';
import { cn } from '@/lib/cn';

/**
 * Pick a month, a quarter or a financial year.
 *
 * ── WHY IT IS A SELECT AND NOT TWO DATE INPUTS ──
 * Nobody reviewing a client's books wants an arbitrary range. They want August,
 * or Q2, or the year - and typing two dates to get August is four keystrokes
 * and a chance to fumble the month end. The custom range is still there for the
 * rare case, one click away, and it defaults to whatever was already chosen so
 * opening it never loses the period.
 *
 * The quarters come from the org's own `fiscalYearStartMonth`, so "Q1" means
 * Apr-Jun for an Indian business and Jan-Mar for one that set January.
 */
export function PeriodPicker({
  value,
  onChange,
  id = 'period',
}: {
  value: Period;
  onChange: (period: Period) => void;
  id?: string;
}) {
  const startMonth = useFiscalYearStartMonth();

  const options = useMemo(() => periodOptions(startMonth), [startMonth]);
  const quick = useMemo(() => quickPeriods(startMonth), [startMonth]);
  const isCurrent = (option: Period) => option.from === value.from && option.to === value.to;

  // The chosen period may be a custom range that is not in the list. Adding it
  // keeps the select showing what is actually being displayed rather than
  // silently snapping back to the first option.
  const known = options.some((option) => option.from === value.from && option.to === value.to);
  const all = known ? options : [{ ...value, key: 'custom' }, ...options];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/*
        ── ONE TAP FOR THE THREE THAT MATTER ──
        The select still holds every month, quarter and year. These are the
        three a person picks nine times in ten, and picking from a select on
        a phone is an open, a scroll and a tap for each. `aria-pressed` says
        which is showing; the select underneath agrees, because both read
        the same `value`.
      */}
      <div className="flex flex-wrap gap-1" role="group" aria-label="Quick periods">
        {quick.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={isCurrent(option)}
            title={option.label}
            onClick={() => onChange({ from: option.from, to: option.to, label: option.label, key: option.key })}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors touch:min-h-11 touch:px-4',
              isCurrent(option)
                ? 'bg-primary/10 text-primary'
                : 'border border-input text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent',
            )}
            data-testid={`period-${option.short.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {option.short}
          </button>
        ))}
      </div>
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        Period
      </label>
      <select
        id={id}
        value={`${value.from}:${value.to}`}
        onChange={(event) => {
          const [from, to] = event.target.value.split(':');
          const picked = all.find((option) => option.from === from && option.to === to);
          if (picked) onChange(picked);
        }}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {all.map((option) => (
          <option key={option.key} value={`${option.from}:${option.to}`}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Wraps: two date fields at the 16px a touch screen gives them are
          wider than a 320px phone, and a row that cannot wrap widens the
          page instead. */}
      <div className="flex flex-wrap items-center gap-1">
        <input
          type="date"
          aria-label="Period start"
          value={value.from}
          max={value.to}
          onChange={(event) =>
            onChange({ ...value, from: event.target.value, label: 'Custom range', key: 'custom' })
          }
          className="h-9 min-w-0 max-w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <input
          type="date"
          aria-label="Period end"
          value={value.to}
          min={value.from}
          onChange={(event) =>
            onChange({ ...value, to: event.target.value, label: 'Custom range', key: 'custom' })
          }
          className="h-9 min-w-0 max-w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm"
        />
      </div>
    </div>
  );
}
